<script setup>
import { ref, computed, onMounted } from 'vue';
import { modal } from '@shop/frontend/web/js/components/Modal';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import useCartStore from '@vueApp/store/modules/cart';
import ITeams from '@components/ITeams/ITeams.vue';
import ITicket from '@components/ITicket/ITicket.vue';
import IPackageEvent from '@components/IPackageEvent/IPackageEvent.vue';
import IPackageEventName from '@components/IPackageEventName/IPackageEventName.vue';
import ExtraInfo from '@components/ExtraInfo.vue';
import IAdditionalsModal from '@components/IAdditionalsModal/IAdditionalsModal.vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import utc from 'dayjs/plugin/utc';

const lang = ref($('html').attr('lang') || 'ru');
dayjs.locale(lang.value);
dayjs.extend(utc);

const cartStore = useCartStore();
const props = defineProps({
  itemCase: {
    type: Object,
    required: true,
  },
  trashButton: {
    type: Boolean,
    default: true,
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
  isSingle: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['removeEvent']);

const eventDate = computed(() => dayjs.tz(props.itemCase.startTimestamp * 1000, CURRENT_TIMEZONE));

function removeTicket(ticket, eventId, type) {
  const adultTicketsCount = props.itemCase.tickets.reduce(
    (ticketsCount, currentTicket) => (currentTicket.loyaltyCategoryId === null ? ticketsCount + 1 : ticketsCount),
    0,
  );

  const hasChildTicket = props.itemCase.tickets.some(
    (currentTicket) => currentTicket.loyaltyCategoryId === 1,
  );

  if (hasChildTicket && adultTicketsCount <= 1 && !ticket.loyaltyCategoryId) {
    modal.error(translator.t('order', 'Need at least one adult ticket!'));
    return;
  }

  if (type === 'service_simple') {
    cartStore.removeServiceTicket(ticket, eventId);
  } else {
    cartStore.removeEventTicket(ticket, eventId);
  }
}

function removeEvent(eventId, type, countTickets) {
  if (type === 'service_simple') {
    cartStore.removeService(eventId, props.itemCase.orderId, countTickets);
  } else {
    cartStore.removeEvent(eventId, props.itemCase.orderId);
  }
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
  cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}

const isPackageEventsShow = ref(false);

/* Доп. услуги: кнопка под билетом есть только если у билета есть услуги.
   БО отдаёт список услуг только по конкретной позиции сделки (index = uid
   билета), «по всей сделке» запрос падает («Не задана переменная
   @InGendocItemID») — поэтому проверяем каждый билет отдельно.
   Ошибки игнорируем — услуги не должны мешать оформлению заказа */
const additionalsByTicket = ref({});
const openedAdditionalsTicket = ref(null);

const isAdditionalsAvailable = computed(() => !props.isHistory && props.itemCase.type === 'vagrant');
const isAnyAdditionals = computed(() => Object.keys(additionalsByTicket.value).length > 0);

onMounted(() => {
  if (!isAdditionalsAvailable.value) return;

  props.itemCase.tickets.forEach(ticket => {
    cartStore.getAdditionals(props.itemCase.id, ticket.uid)
      .then(res => {
        /* Кнопка нужна и для удаления уже добавленной к билету услуги */
        const hasAdded = (res.added || []).some(
          service => service.parent != null && Number(service.parent) === Number(ticket.uid),
        );

        if (res.additionals.length > 0 || hasAdded) {
          additionalsByTicket.value[ticket.uid] = true;
        }
      });
  });
});

function openAdditionalsModal(ticket) {
  openedAdditionalsTicket.value = ticket;
}

/* Суффикс счётчика добавленных подарков: « (+2)», если они есть */
function addedGiftsSuffix(ticket) {
  const count = cartStore.addedAdditionalsCount(props.itemCase.id, ticket.uid);

  return count ? ` (+${count})` : '';
}
</script>

<template>
  <div class="i-card-event-item">
    <template v-if="!isSingle">
      <template v-if="itemCase.type == 'vagrant'">
        <a
          class="i-card-event-item__name i-card-event-item__name_history"
          :href="itemCase.pageLink"
        >
          <IPackageEventName
            v-if="itemCase.eventType === 'Пакет' && itemCase.participants?.team"
            :package-event="itemCase"
          />
          <ITeams
            v-else-if="itemCase.participants?.team"
            :first-team="itemCase.participants.team.homeTeam"
            :second-team="itemCase.participants.team.guestTeam"
            :is-history="true"
            :is-home-logo-with-opponent="itemCase?.isHomeLogoWithOpponent"
            :is-home-logo-without-opponent="itemCase?.isHomeLogoWithoutOpponent"
            :is-show-tournament="itemCase?.isShowTournament"
            :name="itemCase?.name"
          />
          <div v-else class="name">
            <div class="name__img">
              <img :src="itemCase.participants?.singular.imageUrl" alt="eventImage">
            </div>
            <div class="name__title">
              {{ itemCase.participants?.singular.name }}
            </div>
          </div>
        </a>
        <div v-if="itemCase.eventType !== 'Пакет'" class="i-card-event-item__details">
          <span class="time">
            <svg width="20" height="20">
              <use xlink:href="#calendar" />
            </svg>
            <span class="text-capitalize">{{ dayjs(itemCase.startTimestampStr).format("DD.MM.YYYY HH:mm") }}</span>
          </span>
          <span class="place">
            <svg width="20" height="20">
              <use xlink:href="#local" />
            </svg>
            {{ itemCase.unitName }}
          </span>
        </div>
      </template>
      <template v-else>
        <div
          class="i-card-event-item__name"
          :class="{ 'i-card-event-item__name_service': itemCase.type === 'service_simple' }"
        >
          <div class="name">
            <div class="name__title">{{ itemCase.name }}</div>
          </div>
        </div>
      </template>

      <button
        v-if="itemCase.eventType === 'Пакет'"
        class="i-card-event-item__show-packages-btn"
        :class="{ 'i-card-event-item__show-packages-btn_active': isPackageEventsShow }"
        @click="isPackageEventsShow = !isPackageEventsShow"
      >
        <svg height="24" width="24">
          <use xlink:href="#chevron-down" />
        </svg>
      </button>

      <div class="i-card-event-item__summary-price">
        <p>Цена:</p>
        <span>{{ itemCase.summaryPrice }} ₽</span>
      </div>
      <button
        v-if="trashButton"
        class="i-card-event-item__delete"
        @click="
          removeEvent(itemCase.id, type ?? itemCase.type, ticketsCount ?? itemCase.tickets.length)
        "
      >
        <p>Удалить</p>
        <svg width="20" height="20">
          <use xlink:href="#basket" />
        </svg>
      </button>
      <div
        v-if="itemCase?.packageEvents?.length"
        v-show="isPackageEventsShow"
        class="i-card-event-item__package-events"
      >
        <IPackageEvent
          v-for="packageEvent in itemCase.packageEvents"
          :key="packageEvent.startTimestamp"
          :package-event="packageEvent"
        />
      </div>
    </template>

    <div class="i-card-event-item__tickets">
      <slot>
        <div v-for="ticket in itemCase.tickets" :key="ticket.place" class="tickets-item">
          <i-ticket
            :ticket="ticket"
            :is-history="isHistory"
            :inactive="false"
            :short="false"
            :allowed-loyalty="itemCase.allowedLoyaltyCategories"
            @remove-ticket="(curTicket) => removeTicket(curTicket, itemCase.id, itemCase.type)"
            @change-loyalty="
              ({ ticket, categoryId, priceId }) =>
                changeLoyalty(ticket, itemCase.id, categoryId, priceId)
            "
          >
            <ExtraInfo
              v-if="ticket.isEnableExtraInfo"
              :key="ticket.uid"
              :ticket="ticket"
              :is-history="isHistory"
            />
          </i-ticket>
          <button
            v-if="additionalsByTicket[ticket.uid]"
            class="tickets-item__additionals-btn reset-btn"
            type="button"
            @click="openAdditionalsModal(ticket)"
          >
            Добавить подарок{{ addedGiftsSuffix(ticket) }}
          </button>
        </div>
      </slot>
    </div>

    <i-additionals-modal
      v-if="isAnyAdditionals"
      :model-value="openedAdditionalsTicket !== null"
      :ticket="openedAdditionalsTicket"
      :event-id="itemCase.id"
      :order-id="itemCase.orderId"
      @update:model-value="value => { if (!value) openedAdditionalsTicket = null; }"
    />
  </div>
</template>

<style lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
.fade-enter-active,
.fade-leave-active {
  transition: height 0.5s ease-in-out;
}

.fade-enter-to,
.fade-leave-from {
  overflow: hidden;
  height: 100%;
}

.fade-enter-from,
.fade-leave-to {
  overflow: hidden;
  height: 0;
}

.i-card-event-item {
  display: grid;
  grid-template-rows: auto auto;
  grid-template-areas:
    "details details teams teams . price"
    "tickets tickets tickets tickets tickets delete";
  border: 1px solid $light-gray;
  border-radius: 8px;
  padding: 20px;
  gap: 20px;
  overflow: hidden;
  margin-top: 20px;

  @media screen and (max-width: $screen-md-min) {
    grid-template-columns: 1fr 1fr;
    grid-auto-flow: column;
    grid-template-areas:
      "teams teams"
      "details details"
      "tickets tickets"
      "price delete";
  }

  &__name {
    grid-area: teams;

    @media screen and (max-width: $screen-md-min) {
      background-color: $black;
      margin: -20px;
      margin-bottom: 0;
      width: calc(100% + 40px);
      padding: 16px;

      &_history {
        .i-teams__item {
          text-align: center;
        }
      }
    }

    .name {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      justify-content: center;

      .name__img {
        width: 60px;
        height: 60px;
        object-fit: cover;

        img {
          max-height: 100%;
        }

        @media screen and (max-width: $screen-md-min) {
          width: 32px;
          height: 32px;
        }
      }

      .name__title {
        color: $black;
        font-weight: 700;

        @media screen and (max-width: $screen-md-min) {
          color: $white;
          font-size: 14px;
        }
      }
    }
  }

  &__details {
    grid-area: details;
    display: flex;
    flex-direction: column;

    .time {
      font-weight: 700;
    }

    .time,
    .place {
      display: flex;
      align-items: center;
      font-size: 14px;

      svg {
        width: 20px;
        height: 20px;
        min-width: 20px;
        margin-right: 8px;
        margin-top: -1px;
        color: $dark-grey;
      }
    }
  }

  &__tickets {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    grid-area: tickets;

    .tickets-item {
      margin-right: 16px;
      margin-bottom: 8px;
      position: relative;

      @media screen and (max-width: $screen-small) {
        width: 100%;
        margin-right: 0;
      }

      &__additionals-btn {
        margin-top: 8px;
        width: 100%;
        padding: 4px 12px;
        border: 1px solid $primary;
        border-radius: 8px;
        background-color: $primary;
        color: $white;
        font-size: 13px;
        white-space: nowrap;

        &:hover {
          background-color: $light-orange;
        }
      }
    }
  }

  &__delete {
    grid-area: delete;
    display: flex;
    flex-direction: row;
    color: $error;
    align-self: flex-end;
    justify-self: flex-end;
    align-items: center;
    gap: 12px;

    p {
      margin: 0;

      @media screen and (max-width: $screen-md-min) {
        display: none;
      }
    }
  }

  &__summary-price {
    grid-area: price;
    justify-self: flex-end;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;

    p {
      margin: 0;
      font-size: 14px;
    }

    span {
      font-weight: 700;
      font-size: 20px;
      line-height: 120%;
    }

    @media screen and (max-width: $screen-md-min) {
      justify-self: start;
    }
  }
}
</style>
